/**
 * Finishing an email address for somebody who is holding a queue up (§2.2-3).
 *
 * Almost every address a shop takes ends in one of a dozen domains. Typing
 * `@gmail.com` costs ten keystrokes at a counter, hundreds of times a season,
 * and it is the step at which staff quietly stop asking for an email at all —
 * which costs the shop the thing that makes a no-receipt return findable
 * (§9.1, decision 74).
 *
 * **Suggestions, not autocomplete.** An inline ghost completion that fights the
 * next keystroke is worse than nothing on a touch screen: it guesses while
 * somebody is still typing, and the wrong guess is committed by the Enter they
 * were going to press anyway. This offers finished addresses to *tap* — visible,
 * ignorable, and one interaction when it is right. Recognition over recall, and
 * over prediction.
 *
 * The list is ordered by how common the domain actually is on a US consumer
 * counter, because the first suggestion is the one that gets pressed.
 */

const DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'comcast.net',
  'me.com',
  'live.com',
  'msn.com',
  'sbcglobal.net',
  'att.net',
  'verizon.net',
  'cox.net',
] as const;

/** How many to offer. More than four is a menu, and a menu is a decision. */
const MAX_SUGGESTIONS = 4;

/**
 * Finished addresses to offer for what has been typed so far.
 *
 * Empty until there is an `@` and something before it — suggesting a domain
 * before there is a mailbox to put in front of it is noise. Empty again once
 * the address already ends in one of them, because there is nothing left to
 * offer and a suggestion identical to the field is a control that does nothing
 * (§2.2-13).
 */
export function emailDomainSuggestions(value: string): string[] {
  const trimmed = value.trim().toLowerCase();
  const at = trimmed.indexOf('@');

  // No `@` yet, or nothing in front of it: there is nothing to complete.
  if (at <= 0) return [];
  // Two `@` is a typo, not an address; guessing at it would be guessing wrong.
  if (trimmed.indexOf('@', at + 1) !== -1) return [];

  const mailbox = trimmed.slice(0, at);
  const partial = trimmed.slice(at + 1);

  // Already finished. Offering "gmail.com" to somebody who typed it is a row
  // that cannot change anything.
  if (DOMAINS.some((domain) => domain === partial)) return [];

  return DOMAINS.filter((domain) => domain.startsWith(partial))
    .slice(0, MAX_SUGGESTIONS)
    .map((domain) => `${mailbox}@${domain}`);
}

/**
 * Loose enough to accept a real address, strict enough to catch a typo.
 *
 * Deliberately not an RFC 5322 validator: those accept things no mail server
 * will, reject things that work, and are impossible to explain to somebody at a
 * counter. Something, an `@`, something, a dot, something — which is what
 * everybody means by "an email address".
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}
